<script setup lang="ts">
import { ref, watch } from 'vue'
import { BaseModal, BaseButton } from '@/components'
import type { LeadConvertDto } from '../api/types'

const props = defineProps<{
    modelValue: boolean
    initialEstimatedValue?: number
    isSubmitting?: boolean
}>()

const emit = defineEmits<{
    'update:modelValue': [value: boolean]
    'convert': [payload: LeadConvertDto]
}>()

const estimatedValue = ref<number | undefined>(props.initialEstimatedValue)
const convertedOpportunityId = ref('')

watch(() => props.modelValue, (isOpen) => {
    if (isOpen) {
        estimatedValue.value = props.initialEstimatedValue
        convertedOpportunityId.value = ''
    }
})

function handleConvert() {
    emit('convert', {
        estimatedValue: estimatedValue.value,
        convertedOpportunityId: convertedOpportunityId.value.trim() || undefined,
    })
}
</script>

<template>
    <BaseModal
        :model-value="modelValue"
        title="Convert Lead to Opportunity"
        size="md"
        @update:model-value="(v) => emit('update:modelValue', v)"
    >
        <div class="convert-modal">
            <p class="convert-modal__desc">
                Advance this qualified prospect to an active opportunity. The lead status will change to
                <span class="badge badge--converted">Converted</span>.
            </p>

            <div class="form-group">
                <label class="form-label" for="opp-value">Estimated Value ($ USD)</label>
                <input
                    id="opp-value"
                    v-model.number="estimatedValue"
                    type="number"
                    min="0"
                    step="100"
                    class="form-input"
                    placeholder="e.g. 150000"
                />
            </div>

            <div class="form-group">
                <label class="form-label" for="opp-ref">Opportunity ID / External Ref (Optional)</label>
                <input
                    id="opp-ref"
                    v-model="convertedOpportunityId"
                    type="text"
                    class="form-input"
                    placeholder="e.g. OPP-2026-0042"
                />
                <span class="form-hint">Reference identifier in your external pipeline or CRM</span>
            </div>
        </div>

        <template #footer>
            <div class="modal-actions">
                <button
                    type="button"
                    class="action-link"
                    :disabled="isSubmitting"
                    @click="emit('update:modelValue', false)"
                >
                    Cancel
                </button>
                <BaseButton
                    variant="primary"
                    :loading="isSubmitting"
                    @click="handleConvert"
                >
                    Confirm Conversion
                </BaseButton>
            </div>
        </template>
    </BaseModal>
</template>

<style scoped>
.convert-modal {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.convert-modal__desc {
    font-size: var(--text-sm);
    color: var(--text-secondary);
    margin: 0;
    line-height: 1.5;
}

.badge--converted {
    display: inline-block;
    padding: 0.125rem 0.375rem;
    background: rgba(16, 185, 129, 0.15);
    color: var(--color-success);
    border-radius: var(--radius-sm);
    font-size: var(--text-xs);
    font-weight: 600;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
}

.form-label {
    font-size: var(--text-xs);
    font-weight: 500;
    color: var(--text-muted);
}

.form-input {
    padding: 0.5rem 0.625rem;
    background: var(--input-bg);
    border: 1px solid var(--input-border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    font-size: var(--text-sm);
    font-family: inherit;
    transition: border-color 0.15s, box-shadow 0.15s;
}

.form-input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(18, 192, 221, 0.15);
}

.form-hint {
    font-size: var(--text-xs);
    color: var(--text-disabled);
}

.modal-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    width: 100%;
}

.action-link {
    background: none;
    border: none;
    color: var(--text-secondary);
    font-size: var(--text-sm);
    cursor: pointer;
    padding: 0.375rem 0.75rem;
    transition: color 0.15s;
}

.action-link:hover {
    color: var(--text-primary);
}
</style>
